import React from 'react';
import { isString } from 'lodash';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { remCalc } from '../../util/style-util';
import StaticMenuLink from './StaticMenuLink';
import WebLink from '../@common/WebLink';
import HWTLink from '../@common/HWTLink';
import ArrowDropdown from '../@common/Dropdowns/ArrowDropdown';
import maybe from '../@common/enhancers/maybe';

export type LinkType = 'prekitt' | 'web' | 'static';

export type Props = {
  name: string;
  link: string;
  icon: string;
  subItems: any;
  children?: any;
  onClick?: () => void;
  linkType: LinkType;
  activeColor: string;
  handleItemClick: () => void;
  toggleSubmenu: (string) => void;
  isSubmenuItem: boolean;
  keepSubmenuState: boolean;
  renderChildren: boolean;
  accessibilityLabelDescription: string;
  accessibilityLabelButton: (boolean) => string;
  disabled: boolean;
};

const linkTypes = {
  web: WebLink,
  hitt: HWTLink,
  static: StaticMenuLink
};

const MenuItem = ({
  subItems,
  accessibilityLabelButton,
  keepSubmenuState,
  renderChildren,
  toggleSubmenu,
  name,
  link,
  icon: Icon,
  handleItemClick,
  linkType,
  activeColor,
  isSubmenuItem,
  accessibilityLabelDescription,
  disabled
}) => {
  const open = keepSubmenuState ? renderChildren : false;

  const { menuItem, iconWrapper, menuLink, menuButton, leftPadding, smallText, disabledItem } =
    useStyles()({ activeColor });

  const handleDropdownState = () => {
    renderChildren ? toggleSubmenu('') : toggleSubmenu(name);
  };

  const renderContent = (): React.ReactElement => {
    const MaybeDropdown: any = maybe(ArrowDropdown);

    return (
      <div>
        <MaybeDropdown
          shouldRender={!!subItems}
          open={open}
          onClick={handleDropdownState}
          accessibilityLabelButton={accessibilityLabelButton}
        />
        {open ? subItems : null}
      </div>
    );
  };

  const MenuLink = linkTypes[linkType] || linkTypes['hitt'];

  return (
    <li className={clsx(menuItem, disabled && disabledItem)} onClick={handleItemClick}>
      <div className={clsx(menuButton, isSubmenuItem && leftPadding)}>
        <span className={iconWrapper}>
          {isString(Icon) ? (
            <img src={Icon} height="29px" width="29px" alt="icon" />
          ) : (
            <Icon height="29px" width="29px" />
          )}
        </span>
        <MenuLink
          to={link}
          className={clsx(menuLink, isSubmenuItem && smallText)}
          name={name}
          activeColor={activeColor}
          accessibilitylabeldescription={accessibilityLabelDescription}
        />
      </div>
      {renderContent()}
    </li>
  );
};

const useStyles = () =>
  makeStyles(() => ({
    menuItem: {
      width: '100%',
      position: 'relative',
      whiteSpace: 'nowrap',
      padding: '4px 0px',
      fontSize: '15px !important'
    },
    leftPadding: {
      paddingLeft: `${remCalc(30)} !important`,
      fontSize: '10px !important'
    },
    menuButton: {
      display: 'flex',
      alignItems: 'center',
      paddingLeft: '15px'
    },
    iconWrapper: {
      width: remCalc(29)
    },
    menuLink: ({ activeColor }: any) =>
      ({
        textDecoration: 'none',
        fontSize: '15px !important',
        padding: '10px !important',
        marginLeft: '10px !important',
        color: 'white',
        '&:hover': {
          color: `${activeColor} !important`,
          textDecoration: 'none',
          cursor: 'pointer'
        },
        '&:focus': {
          color: `${activeColor} !important`,
          textDecoration: 'none',
          cursor: 'pointer'
        }
      } as any),
    smallText: {
      fontSize: '0.9rem !important'
    },
    disabledItem: {
      opacity: '0.4 !important',
      pointerEvents: 'none'
    }
  }));

export default MenuItem;
